import { Layout } from "@/components/layout/Layout";
import { PageHero } from "@/components/shared/PageHero";
import { SectionLabel } from "@/components/shared/SectionLabel";
import { RevealText } from "@/components/shared/RevealText";
import { AnimatedSection } from "@/components/shared/AnimatedSection";
import { CTASection } from "@/components/shared/CTASection";
import { ShieldCheck, Sparkle, ChatCircleText, UsersThree } from "@phosphor-icons/react";
import { VALUES } from "@/data/siteData";

const HERO_IMAGE =
  "https://images.unsplash.com/photo-1659355894748-0b7b60de60b6?crop=entropy&cs=srgb&fm=jpg&q=80&w=1600";
const STORY_IMAGE =
  "https://images.unsplash.com/photo-1528082414335-adbd64f18d12?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200";

const VALUE_ICONS = { ShieldCheck, Sparkle, ChatCircleText, UsersThree };

export default function About() {
  return (
    <Layout
      title="About Us | Mithra Jobs"
      description="Mithra Jobs was established in 2011 and focuses on teacher recruitment for schools, connecting schools with suitable teaching professionals."
    >
      <PageHero
        label="About Us"
        heading="Experienced in Recruitment. Focused on Education."
        description="Mithra Jobs was established in 2011 with a focus on teacher recruitment for schools. We help schools connect with teaching professionals who match their staffing requirements."
        image={HERO_IMAGE}
        imageAlt="Professional educator at work"
      />

      <section className="py-24 px-6 bg-cream">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 items-start">
          <div>
            <SectionLabel>Our Story</SectionLabel>
            <RevealText
              as="h2"
              text="A Focus on Education Recruitment Since 2011"
              className="font-heading text-3xl sm:text-4xl font-semibold text-navy tracking-tight leading-tight"
            />
            <div className="mt-12 space-y-10">
              <AnimatedSection className="flex gap-6" data-testid="timeline-2011">
                <span className="font-heading text-2xl text-accent flex-shrink-0 w-20">2011</span>
                <p className="text-slate leading-relaxed border-l border-navy/10 pl-6">
                  Mithra Jobs begins its journey, focused on teacher recruitment for schools.
                </p>
              </AnimatedSection>
              <AnimatedSection delay={0.1} className="flex gap-6" data-testid="timeline-today">
                <span className="font-heading text-2xl text-accent flex-shrink-0 w-20">Today</span>
                <p className="text-slate leading-relaxed border-l border-navy/10 pl-6">
                  Mithra Jobs continues to focus on connecting schools with teaching professionals.
                </p>
              </AnimatedSection>
            </div>
          </div>
          <AnimatedSection delay={0.15}>
            <img
              src={STORY_IMAGE}
              alt="Experienced educator in a school setting"
              className="rounded-2xl w-full h-[460px] object-cover shadow-lg"
            />
          </AnimatedSection>
        </div>
      </section>

      <section className="py-24 px-6 bg-white border-t border-navy/10">
        <div className="max-w-4xl mx-auto">
          <SectionLabel>Our Approach</SectionLabel>
          <RevealText
            as="h2"
            text="We Understand That Every School Is Different"
            className="font-heading text-3xl sm:text-4xl font-semibold text-navy tracking-tight leading-tight"
          />
          <AnimatedSection delay={0.15}>
            <p className="mt-6 text-slate text-base sm:text-lg leading-relaxed">
              Schools have different academic environments, curricula, student communities and staffing
              requirements. Mithra Jobs works as a recruitment partner that listens to each school&rsquo;s needs
              before recommending candidates, so the professionals we present are relevant to the role you are
              looking to fill.
            </p>
          </AnimatedSection>
        </div>
      </section>

      <section className="py-24 px-6 bg-navy">
        <div className="max-w-3xl mx-auto text-center">
          <div className="flex justify-center">
            <SectionLabel light>Our Mission</SectionLabel>
          </div>
          <RevealText
            as="h2"
            text="Helping Schools Build Strong Teaching Teams"
            className="font-heading text-3xl sm:text-4xl font-semibold text-cream tracking-tight leading-tight"
          />
          <AnimatedSection delay={0.15}>
            <p className="mt-6 text-cream/75 text-base sm:text-lg leading-relaxed">
              Our mission is to help schools build strong teaching teams by connecting them with suitable teaching
              professionals for their requirements.
            </p>
          </AnimatedSection>
        </div>
      </section>

      <section className="py-24 px-6 bg-cream">
        <div className="max-w-7xl mx-auto">
          <SectionLabel>Our Values</SectionLabel>
          <RevealText
            as="h2"
            text="What Guides Our Work"
            className="font-heading text-3xl sm:text-4xl font-semibold text-navy tracking-tight leading-tight mb-14"
          />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {VALUES.map((v, i) => {
              const Icon = VALUE_ICONS[v.icon];
              return (
                <AnimatedSection
                  key={v.title}
                  delay={i * 0.08}
                  data-testid={`value-card-${v.title.toLowerCase().replace(/\s+/g, "-")}`}
                  className="bg-white rounded-2xl border border-navy/10 p-8"
                >
                  <Icon size={26} weight="duotone" className="text-accent mb-5" />
                  <h3 className="font-heading text-lg font-semibold text-navy mb-2">{v.title}</h3>
                  <p className="text-sm text-slate leading-relaxed">{v.description}</p>
                </AnimatedSection>
              );
            })}
          </div>
        </div>
      </section>

      <CTASection
        heading="Have a Teaching Requirement?"
        description="Tell us about the positions you are looking to fill and our team can help you identify suitable teaching professionals."
        primaryLabel="Talk to Mithra Jobs"
        primaryTo="/contact-us"
        testIdPrefix="about-cta"
      />
    </Layout>
  );
}
